import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut, ExternalLink, LayoutDashboard, FileText, Image, BedDouble, UtensilsCrossed,
  Compass, Tag, Images, Newspaper, Star, Inbox, Settings, Link2, Users, Menu, X, Megaphone,
} from "lucide-react";
import { api } from "../../lib/api";
import { LogoMark } from "../../components/Logo";
import CollectionTab from "./CollectionTab";
import SettingsTab from "./SettingsTab";
import HeroModePanel from "./HeroModePanel";
import SeoTab from "./SeoTab";
import SectionsTab from "./SectionsTab";
import DashboardTab from "./DashboardTab";
import InquiriesTab from "./InquiriesTab";
import UsersTab from "./UsersTab";
import Seo from "../../components/Seo";

const ALL = ["super_admin", "admin", "editor"];
const GROUPS = [
  { label: "Overview", items: [{ id: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ALL }] },
  {
    label: "Pages & Content",
    items: [
      { id: "sections", label: "Page Sections", icon: FileText, roles: ALL },
      { id: "hero", label: "Hero Media", icon: Image, roles: ALL },
      { id: "villas", label: "Villas & Suites", icon: BedDouble, roles: ALL },
      { id: "dining", label: "Dining", icon: UtensilsCrossed, roles: ALL },
      { id: "experiences", label: "Experiences", icon: Compass, roles: ALL },
      { id: "offers", label: "Offers", icon: Tag, roles: ALL },
      { id: "popups", label: "Promo Popups", icon: Megaphone, roles: ALL },
      { id: "gallery", label: "Gallery", icon: Images, roles: ALL },
      { id: "blog", label: "Blog", icon: Newspaper, roles: ALL },
      { id: "reviews", label: "Guest Reviews", icon: Star, roles: ALL },
    ],
  },
  {
    label: "Manage",
    items: [
      { id: "inquiries", label: "Inquiries", icon: Inbox, roles: ALL },
      { id: "users", label: "Users & Roles", icon: Users, roles: ["super_admin"] },
    ],
  },
  {
    label: "Configure",
    items: [
      { id: "settings", label: "Settings & Booking", icon: Settings, roles: ALL },
      { id: "seo", label: "SEO & Links", icon: Link2, roles: ALL },
    ],
  },
];

const ALL_TABS = GROUPS.flatMap((g) => g.items);

const AdminBrand = () => (
  <div data-testid="admin-brand-lockup" className="flex items-center gap-3 min-w-0">
    <span className="w-10 h-10 rounded-full bg-[#9A7B66]/12 border border-[#D8CBB8]/20 flex items-center justify-center shrink-0">
      <LogoMark className="w-6 h-6" color="#D8CBB8" />
    </span>
    <div className="min-w-0">
      <p className="text-[13px] font-sans font-bold tracking-[0.24em] text-white leading-none">NUSADIRA</p>
      <p data-testid="admin-console-label" className="mt-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D8CBB8]/65 font-sans font-semibold whitespace-nowrap">
        Management Console
      </p>
    </div>
  </div>
);


export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("nl_token");
        navigate("/console/login");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("nl_token");
    navigate("/console/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F4EFEA] flex items-center justify-center">
        <p className="text-sm text-[#A0988E] font-sans">Checking session...</p>
      </div>
    );
  }

  const visibleGroups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((t) => t.roles.includes(user.role)),
  })).filter((g) => g.items.length > 0);
  const visibleTabs = ALL_TABS.filter((t) => t.roles.includes(user.role));
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : "dashboard";
  const activeLabel = visibleTabs.find((t) => t.id === activeTab)?.label || "";

  const NavContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-white/5 bg-white/[0.02]">
        <AdminBrand />
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3" data-lenis-prevent>
        {visibleGroups.map((g) => (
          <div key={g.label}>
            <p className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-[0.25em] text-[#A0988E]/60 font-sans font-semibold">
              {g.label}
            </p>
            {g.items.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}${mobile ? "-mobile" : ""}`}
                onClick={() => {
                  setTab(t.id);
                  setNavOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] font-sans font-medium transition-colors ${
                  activeTab === t.id
                    ? "bg-[#9A7B66] text-white"
                    : "text-[#A0988E] hover:text-white hover:bg-white/5"
                }`}
              >
                <t.icon className="w-4 h-4 shrink-0" />
                {t.label}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#9A7B66] flex items-center justify-center text-white text-sm font-bold font-sans">
            {(user.name || "A")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-sans font-semibold text-white truncate">{user.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#D8CBB8]/70 font-sans">
              {user.role === "super_admin" ? "Super Admin" : user.role}
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="p-2 text-[#A0988E] hover:text-white transition-colors"
            aria-label="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#F4EFEA]">
      <Seo title="Admin Console | Nusadira Lembongan Villa" description="Private content management console for Nusadira Lembongan Villa." noindex />
      <div className="lg:hidden sticky top-0 z-40 bg-[#1E1A17] px-4 h-16 flex items-center justify-between">
        <AdminBrand />
        <div className="flex items-center gap-2">
          <Link
            data-testid="admin-mobile-view-site"
            to="/"
            className="p-2 text-[#D8CBB8] hover:text-white transition-colors"
            aria-label="View website"
          >
            <ExternalLink className="w-5 h-5" />
          </Link>
          <button
          data-testid="admin-mobile-nav-button"
          onClick={() => setNavOpen(true)}
          className="p-2 text-white"
          aria-label="Open admin menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>
      </div>

      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-[#1E1A17] z-40">
        <NavContent />
      </aside>

      <AnimatePresence>
        {navOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setNavOpen(false)}
              className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 left-0 bottom-0 w-72 bg-[#1E1A17] z-[70] lg:hidden"
            >
              <button
                data-testid="admin-mobile-nav-close"
                onClick={() => setNavOpen(false)}
                className="absolute top-5 right-4 p-2 text-white/70 hover:text-white"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
              <NavContent mobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="lg:ml-64">
        <div className="sticky top-16 lg:top-0 z-30 bg-[#F4EFEA]/92 backdrop-blur-md border-b border-[#E5DED4] px-5 sm:px-8 h-16 hidden lg:flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.22em] text-[#A0988E] font-sans font-semibold">Nusadira CMS</p>
            <h1 className="font-serif text-xl text-[#2C2825] leading-tight">{activeLabel}</h1>
          </div>
          <Link
            data-testid="admin-view-site-link"
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-sans font-semibold uppercase tracking-wider text-[#9A7B66] hover:text-[#826552] transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View Site
          </Link>
        </div>
        <div className="p-5 sm:p-8 lg:p-10 max-w-[1600px]">
          {activeTab === "dashboard" && <DashboardTab goTab={setTab} />}
          {activeTab === "sections" && <SectionsTab />}
          {activeTab === "seo" && <SeoTab />}
          {activeTab === "settings" && <SettingsTab />}
          {activeTab === "hero" && (
            <>
              <HeroModePanel />
              <CollectionTab
              resource="hero-slides"
              testPrefix="hero"
              title="Hero Carousel Slides"
              description="Maximum 10 carousel photos. Toggle Live/Hidden, drag the handle to reorder, upload a new image, or choose existing Gallery photos."
              itemTitle="title"
              itemSubtitle="subtitle"
              imageField="image_url"
              maxItems={10}
              reorder
              galleryPicker
              multiImageUpload
              fields={[
                { name: "title", label: "Slide Title", i18n: true },
                { name: "subtitle", label: "Slide Subtitle", i18n: true },
                { name: "image_url", label: "Slide Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
                { name: "order", label: "Display Order", type: "number" },
              ]}
              />
            </>
          )}
          {activeTab === "villas" && (
            <CollectionTab
              resource="villas"
              testPrefix="villas"
              title="Villas & Suites"
              itemTitle="name"
              itemSubtitle="specs"
              imageField="image_url"
              galleryPicker
              fields={[
                { name: "name", label: "Villa Name", i18n: true },
                { name: "price", label: "Price per Night (e.g. $780)" },
                { name: "specs", label: "Specs", type: "textarea", rows: 2, i18n: true },
                { name: "description", label: "Description", type: "textarea", rows: 4, i18n: true },
                { name: "amenities", label: "Amenities & Services (one per line)", type: "textarea", rows: 6 },
                { name: "gallery_images", label: "Villa Gallery - one per line: image URL | alt | SEO title | SEO description | caption | focal point", type: "textarea", rows: 4 },
                { name: "image_url", label: "Villa Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
                { name: "order", label: "Display Order", type: "number" },
              ]}
            />
          )}
          {activeTab === "dining" && (
            <CollectionTab
              resource="dining"
              testPrefix="dining"
              title="Dining"
              itemTitle="title"
              itemSubtitle="note"
              imageField="image_url"
              fields={[
                { name: "title", label: "Dining Title", i18n: true },
                { name: "note", label: "Note (e.g. Daily 7 AM - 10 PM)" },
                { name: "description", label: "Description", type: "textarea", rows: 3, i18n: true },
                { name: "booking_url", label: "Booking Link (optional - uses main Booking Engine URL when empty)" },
                { name: "image_url", label: "Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
                { name: "order", label: "Display Order", type: "number" },
              ]}
            />
          )}
          {activeTab === "experiences" && (
            <CollectionTab
              resource="experiences"
              testPrefix="experiences"
              title="Experiences"
              itemTitle="title"
              itemSubtitle="description"
              imageField="image_url"
              fields={[
                { name: "title", label: "Experience Title", i18n: true },
                { name: "order", label: "Display Order", type: "number" },
                { name: "description", label: "Description", type: "textarea", rows: 3, i18n: true },
                { name: "booking_url", label: "Booking Link (optional - uses main Booking Engine URL when empty)" },
                { name: "image_url", label: "Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
              ]}
            />
          )}
          {activeTab === "offers" && (
            <CollectionTab
              resource="offers"
              testPrefix="offers"
              title="Offers & Packages"
              description="Seasonal offers shown in the Offers section. All currently active offers can rotate inside the promotion popup. Add schedule dates, inclusions, terms, promo code and an optional booking URL for the detail page."
              itemTitle="title"
              itemSubtitle="tag"
              imageField="image_url"
              fields={[
                { name: "title", label: "Offer Title", i18n: true },
                { name: "slug", label: "SEO URL Slug (e.g. stay-4-pay-3)" },
                { name: "tag", label: "Tag Badge (e.g. Direct Booking Privilege)" },
                { name: "start_date", label: "Promotion Start Date", type: "date" },
                { name: "end_date", label: "Promotion End Date", type: "date" },
                { name: "promo_code", label: "Promo Code" },
                { name: "minimum_nights", label: "Minimum Nights", type: "number" },
                { name: "description", label: "Description", type: "textarea", rows: 3, i18n: true },
                { name: "inclusions", label: "What Is Included (one per line)", type: "textarea", rows: 5 },
                { name: "terms", label: "Terms & Conditions (one per line)", type: "textarea", rows: 5 },
                { name: "booking_url", label: "Booking URL (optional - uses main Booking Engine URL when empty)" },
                { name: "image_url", label: "Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
                { name: "order", label: "Display Order", type: "number" },
              ]}
            />
          )}
          {activeTab === "popups" && (
            <CollectionTab
              resource="popups"
              testPrefix="popups"
              title="Promo Popups"
              description="Create one or more promotional popup messages. Active scheduled popups rotate inside the same compact card on the website."
              itemTitle="title"
              itemSubtitle="eyebrow"
              imageField="image_url"
              fields={[
                { name: "title", label: "Popup Title" },
                { name: "eyebrow", label: "Small Label (e.g. Limited Privilege)" },
                { name: "message", label: "Popup Message", type: "textarea", rows: 3 },
                { name: "cta_label", label: "Booking Button Label" },
                { name: "promo_code", label: "Promo Code" },
                { name: "booking_url", label: "Booking URL (optional - uses main Booking Engine URL when empty)" },
                { name: "detail_url", label: "Details URL (optional, e.g. /offer/offer-id)" },
                { name: "start_date", label: "Start Date", type: "date" },
                { name: "end_date", label: "End Date", type: "date" },
                { name: "image_url", label: "Popup Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
                { name: "active", label: "Show in popup rotation", type: "checkbox", hint: "Active and within schedule" },
                { name: "order", label: "Display Order", type: "number" },
              ]}
            />
          )}
          {activeTab === "gallery" && (
            <CollectionTab
              resource="gallery"
              testPrefix="gallery"
              title="Gallery"
              description="Upload and manage up to 50 gallery photos shown on the live website."
              itemTitle="caption"
              imageField="image_url"
              maxItems={50}
              multiImageUpload
              fields={[
                { name: "caption", label: "Caption", i18n: true },
                { name: "order", label: "Display Order", type: "number" },
                { name: "image_url", label: "Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
              ]}
            />
          )}
          {activeTab === "blog" && (
            <CollectionTab
              resource="posts"
              testPrefix="blog"
              title="Blog Posts"
              itemTitle="title"
              itemSubtitle="category"
              imageField="image_url"
              fields={[
                { name: "title", label: "Post Title", i18n: true },
                { name: "category", label: "Category", i18n: true },
                { name: "read_time", label: "Read Time (e.g. 5 min read)" },
                { name: "excerpt", label: "Excerpt", type: "textarea", rows: 3, i18n: true },
                { name: "content", label: "Full Article", type: "textarea", rows: 8, i18n: true },
                { name: "backlinks", label: "Backlinks (SEO) - one per line: Label | https://url", type: "textarea", rows: 3 },
                { name: "image_url", label: "Cover Image", type: "image" },
                { name: "image_alt", label: "Image Alt Text (SEO/accessibility, optional)" },
                { name: "image_title", label: "Image SEO Title (optional)" },
                { name: "image_description", label: "Image SEO Description (optional)", type: "textarea", rows: 2 },
                { name: "image_caption", label: "Photo Caption (optional - bottom right on click)" },
              ]}
            />
          )}
          {activeTab === "reviews" && (
            <CollectionTab
              resource="reviews"
              testPrefix="reviews"
              title="Guest Reviews Moderation"
              description="Add reviews from Tripadvisor, Google or Booking.com. Only Published reviews appear on the website."
              itemTitle="guest_name"
              itemSubtitle="location"
              fields={[
                { name: "guest_name", label: "Guest Name" },
                { name: "location", label: "Guest Location" },
                { name: "source", label: "Source", type: "select", options: ["Tripadvisor", "Google Review", "Booking.com"] },
                { name: "rating", label: "Rating (1-5)", type: "select", options: ["5", "4", "3", "2", "1"] },
                { name: "text", label: "Review Text", type: "textarea", rows: 4, i18n: true },
                { name: "approved", label: "Publish on website", type: "checkbox", hint: "Approved & visible to guests" },
              ]}
            />
          )}
          {activeTab === "inquiries" && <InquiriesTab />}
          {activeTab === "users" && <UsersTab currentUser={user} />}
        </div>
      </main>
    </div>
  );
}
